/**
 * Single source of truth for site URL and business info.
 * Update SITE_URL here once; canonical tags, sitemap.xml, and JSON-LD
 * schema all read from this value.
 */
export const SITE_URL = "https://www.prettyinpinkcleaners.com";

export const BUSINESS = {
  name: "Pretty in Pink Cleaning Service",
  tagline: "A Pretty Clean. Every Time.",
  secondary: "Where a spotless clean comes to you!",
  offers: "Residential & Commercial Cleaning",
  phoneDisplay: "(334) 202-3594",
  phoneHref: "tel:+13342023594",
  email: "prettyinpinkcleaningservice517@yahoo.com",
  emailHref: "mailto:prettyinpinkcleaningservice517@yahoo.com",
  facebook: "https://www.facebook.com/p/Pretty-in-Pink-Cleaning-Service-100089239612621/",
  city: "Wetumpka",
  state: "AL",
  areaServed: [
    "Wetumpka, AL",
    "Millbrook, AL",
    "Prattville, AL",
    "Montgomery, AL",
    "Tallassee, AL",
    "Eclectic, AL",
  ],
} as const;

export type ServiceSlug =
  | "house-cleaning"
  | "deep-cleaning"
  | "move-in-move-out-cleaning"
  | "commercial-office-cleaning"
  | "post-construction-cleaning";

export type ServiceInfo = {
  slug: ServiceSlug;
  name: string;
  short: string;
  blurb: string;
};

export const services: ServiceInfo[] = [
  {
    slug: "house-cleaning",
    name: "House Cleaning",
    short: "Recurring Residential",
    blurb:
      "Weekly, bi-weekly, and monthly cleaning that keeps your home fresh, tidy, and guest-ready all year.",
  },
  {
    slug: "deep-cleaning",
    name: "Deep Cleaning",
    short: "Top-to-Bottom Detail",
    blurb:
      "A thorough top-to-bottom detail clean that refreshes every corner your regular routine misses.",
  },
  {
    slug: "move-in-move-out-cleaning",
    name: "Move-In / Move-Out Cleaning",
    short: "Make a Fresh Start",
    blurb:
      "Start fresh or leave it spotless — a complete clean for empty homes before or after a move.",
  },
  {
    slug: "commercial-office-cleaning",
    name: "Commercial & Office Cleaning",
    short: "Workplace Cleaning",
    blurb:
      "Reliable nightly and weekly cleaning for offices, clinics, salons, and small businesses.",
  },
  {
    slug: "post-construction-cleaning",
    name: "Post-Construction Cleaning",
    short: "After the Dust Settles",
    blurb:
      "Remove construction dust, debris, and residue so your renovated space is truly move-in ready.",
  },
];

export type CitySlug =
  | "wetumpka-al"
  | "millbrook-al"
  | "prattville-al"
  | "montgomery-al"
  | "tallassee-al"
  | "eclectic-al";

export type CityInfo = {
  slug: CitySlug;
  name: string;
  state: "AL";
  blurb: string;
};

export const cities: CityInfo[] = [
  {
    slug: "wetumpka-al",
    name: "Wetumpka",
    state: "AL",
    blurb:
      "Wetumpka sits along the Tallapoosa River just north of Montgomery, known for the crater and its friendly, close-knit small-town feel. We're proud to clean homes and businesses right here in our hometown.",
  },
  {
    slug: "millbrook-al",
    name: "Millbrook",
    state: "AL",
    blurb:
      "Millbrook is a fast-growing community just south of Wetumpka, full of quiet neighborhoods and busy local shops. We bring dependable residential and commercial cleaning to Millbrook door to door.",
  },
  {
    slug: "prattville-al",
    name: "Prattville",
    state: "AL",
    blurb:
      "Prattville, 'The Fountain City,' blends historic charm near Autauga Creek with busy modern neighborhoods. We keep Prattville homes and offices spotless through every season.",
  },
  {
    slug: "montgomery-al",
    name: "Montgomery",
    state: "AL",
    blurb:
      "Montgomery is Alabama's capital and the hub of our region, with everything from downtown offices to suburban family homes. We serve businesses and residences across the greater Montgomery area.",
  },
  {
    slug: "tallassee-al",
    name: "Tallassee",
    state: "AL",
    blurb:
      "Tallassee sits along the Tallapoosa River in Elmore County with a tight-knit, family-friendly community. We provide reliable cleaning for Tallassee homes and small businesses.",
  },
  {
    slug: "eclectic-al",
    name: "Eclectic",
    state: "AL",
    blurb:
      "Eclectic is a small community on the shores of Lake Martin with a relaxed, lakeside way of life. We clean weekend cabins, full-time homes, and local businesses throughout Eclectic.",
  },
];

export const serviceHref = (slug: ServiceSlug) => `/services/${slug}` as const;
export const cityHref = (slug: CitySlug) => `/service-areas/${slug}` as const;

/** Link options (route pattern + params) for type-safe <Link {...}> usage. */
export const serviceLink = (slug: ServiceSlug) => ({
  to: "/services/$slug" as const,
  params: { slug },
});
export const cityLink = (slug: CitySlug) => ({
  to: "/service-areas/$slug" as const,
  params: { slug },
});
